class SessionService {
  private static readonly TOKEN_KEY = '@ligchat/token';
  private static readonly USER_KEY = '@ligchat/user';
  private static readonly SECTOR_KEY = '@ligchat/sector';
  private static readonly SECTOR_OFFICIAL_KEY = '@ligchat/sector_official';
  private static readonly AVAILABLE_SECTORS_KEY = '@ligchat/available_sectors';

  // Salva um valor na sessão
  static setSession(key: string, value: any): void {
    localStorage.setItem(key, JSON.stringify(value));
  }

  // Decodifica um token JWT
  static decodeToken(token: string): any | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  }

  /**
   * Obtém role e roleValue do payload do token.
   * Busca por várias chaves possíveis (JWT pode usar camelCase, PascalCase ou URIs do .NET).
   */
  private static getRoleFromPayload(payload: Record<string, unknown>): { roleValue?: number; role?: string } {
    const keys = Object.keys(payload);
    const keyMap: Record<string, string> = {};
    keys.forEach((k) => { keyMap[k.toLowerCase()] = k; });

    const roleValueKey = keyMap['rolevalue'] ?? keyMap['role_value'];
    const roleKey = keyMap['role'] ?? keys.find((k) => k.toLowerCase().endsWith('/role'));

    const rawRoleValue = roleValueKey != null ? payload[roleValueKey] : undefined;
    const rawRole = roleKey != null ? payload[roleKey] : undefined;

    // role pode vir como array do .NET (ex: ["SuperAdmin"])
    const role = rawRole != null
      ? (Array.isArray(rawRole) ? rawRole[0] : rawRole)
      : undefined;

    return {
      roleValue: rawRoleValue != null && rawRoleValue !== '' ? Number(rawRoleValue) : undefined,
      role: role != null ? String(role) : undefined
    };
  }

  static isTokenExpired(token: string): boolean {
    try {
      const payload = this.decodeToken(token);
      if (!payload || !payload.exp) return true;
      return Date.now() >= payload.exp * 1000;
    } catch {
      return true;
    }
  }

  // Recupera um item da sessão para o setor
  static getSessionForSector(): any | null {
    const sessionData = localStorage.getItem("selectedSector");
    if (!sessionData) {
      return null;
    }

    return JSON.parse(sessionData); // Retorna qualquer tipo
  }

  // Limpa a sessão do setor
  static clearSectorSession(): void {
    this.removeSession("selectedSector");
  }

  // Recupera um item específico da sessão
  static getSession(key: string): any | null {
    const value = localStorage.getItem(key);
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  // Remove um item específico da sessão
  static removeSession(key: string): void {
    localStorage.removeItem(key);
  }

  // Limpa toda a sessão armazenada
  static clearAllSessions(): void {
    localStorage.clear();
  }

  // Verifica se uma sessão específica está expirada
  static isSessionExpired(key: string): boolean {
    const sessionData = localStorage.getItem(key);
    if (!sessionData) {
      return true;
    }

    const parsedData = JSON.parse(sessionData);
    const token = parsedData;

    if (!token) {
      return true;
    }

    const decodedToken = this.decodeToken(token);
    if (!decodedToken || !decodedToken.exp) {
      return true;
    }

    const currentTimeInSeconds = Math.floor(new Date().getTime() / 1000);
    return decodedToken.exp < currentTimeInSeconds;
  }

  static setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  static getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  static removeToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
  }

  static setUser(user: any): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  static getUser(): any | null {
    const user = localStorage.getItem(this.USER_KEY);
    return user ? JSON.parse(user) : null;
  }

  static removeUser(): void {
    localStorage.removeItem(this.USER_KEY);
  }

  static setAvailableSectors(sectors: number[]) {
    localStorage.setItem(this.AVAILABLE_SECTORS_KEY, JSON.stringify(sectors));
  }

  static getAvailableSectors(): number[] {
    const sectors = localStorage.getItem(this.AVAILABLE_SECTORS_KEY);
    return sectors ? JSON.parse(sectors) : [];
  }

  static setSectorId(sectorId: number, isOfficial: boolean) {
    // Validar e converter os valores
    if (!sectorId || typeof sectorId !== 'number') {
      return;
    }

    const officialValue = Boolean(isOfficial);

    // Verificar se o setor atual é diferente do novo
    const currentSectorId = this.getSectorId();
    const currentIsOfficial = this.getSectorIsOfficial();

    if (currentSectorId === sectorId && currentIsOfficial === officialValue) {
      return; // Se for o mesmo setor e mesmo status, não faz nada
    }

    // Salvar os valores
    localStorage.setItem(this.SECTOR_KEY, sectorId.toString());
    localStorage.setItem(this.SECTOR_OFFICIAL_KEY, officialValue.toString());

    // Atualizar setores disponíveis se necessário
    const availableSectors = this.getAvailableSectors();
    if (!availableSectors.includes(sectorId)) {
      this.setAvailableSectors([...availableSectors, sectorId]);
    }

    // Disparar evento de mudança apenas se houve mudança real
    const event = new CustomEvent('sectorChanged', {
      detail: { sectorId, isOfficial: officialValue }
    });
    window.dispatchEvent(event);
  }

  static getSectorId(): number | null {
    const sectorId = localStorage.getItem(this.SECTOR_KEY);
    if (!sectorId) return null;

    try {
      const parsedId = parseInt(sectorId);
      if (isNaN(parsedId)) return null;
      return parsedId;
    } catch {
      return null;
    }
  }

  static getSectorIsOfficial(): boolean | null {
    const isOfficial = localStorage.getItem(this.SECTOR_OFFICIAL_KEY);
    if (!isOfficial) return null;

    try {
      return isOfficial === 'true';
    } catch {
      return null;
    }
  }

  static removeSectorId() {
    localStorage.removeItem(this.SECTOR_KEY);
    localStorage.removeItem(this.SECTOR_OFFICIAL_KEY);

    const event = new CustomEvent('sectorChanged', {
      detail: { sectorId: null, isOfficial: null }
    });
    window.dispatchEvent(event);
  }

  static validateAndCleanSectorSession(availableSectors: number[]) {
    if (!Array.isArray(availableSectors)) {
      return;
    }

    this.setAvailableSectors(availableSectors);
    const currentSectorId = this.getSectorId();

    if (currentSectorId && !availableSectors.includes(currentSectorId)) {
      this.removeSectorId();
    }
  }

  static isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    return !this.isTokenExpired(token);
  }

  static clearSession() {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.SECTOR_KEY);
    localStorage.removeItem(this.SECTOR_OFFICIAL_KEY);
    localStorage.removeItem(this.AVAILABLE_SECTORS_KEY);
  }

  static setIsAnonymous(isAnonymous: boolean) {
    localStorage.setItem('@ligchat/is_anonymous', JSON.stringify(isAnonymous));
  }

  static getIsAnonymous(): boolean {
    const value = localStorage.getItem('@ligchat/is_anonymous');
    return value ? JSON.parse(value) : false;
  }

  // Função para verificar se o usuário atual é admin (roleValue >= 2)
  // Admin e SuperAdmin devem ver Configurações (Acessos, Setores, Assistente IA)
  static isCurrentUserAdmin(): boolean {
    try {
      const token = this.getToken();
      if (!token) return false;

      const decodedToken = this.decodeToken(token) as Record<string, unknown> | null;
      if (!decodedToken) return false;

      const { roleValue, role } = this.getRoleFromPayload(decodedToken);

      // Priorizar roleValue numérico (2=Admin, 3=SuperAdmin, 4=PlatformAdmin)
      if (roleValue !== undefined && !Number.isNaN(roleValue)) {
        return roleValue >= 2;
      }

      // Fallback: role string (pode ser array do .NET)
      const roleStr = role ? String(role).toLowerCase() : '';
      if (roleStr) {
        return roleStr === 'admin' || roleStr === 'superadmin' || roleStr === 'platformadmin';
      }

      return false;
    } catch (error) {
      return false;
    }
  }

  // Função para obter o role do usuário atual
  static getUserRole(): 'superadmin' | 'admin' | 'collaborator' {
    try {
      const token = this.getToken();
      if (!token) return 'collaborator';

      const decodedToken = this.decodeToken(token) as Record<string, unknown> | null;
      if (!decodedToken) return 'collaborator';

      const { roleValue, role } = this.getRoleFromPayload(decodedToken);

      // Priorizar roleValue numérico (1=Collaborator, 2=Admin, 3=SuperAdmin, 4=PlatformAdmin)
      if (roleValue !== undefined && !Number.isNaN(roleValue)) {
        if (roleValue >= 3) return 'superadmin';
        if (roleValue === 2) return 'admin';
        return 'collaborator';
      }

      // Fallback: role string (pode ser array do .NET)
      const roleStr = role ? String(role).toLowerCase() : '';
      if (roleStr === 'superadmin') return 'superadmin';
      if (roleStr === 'admin') return 'admin';

      return 'collaborator';
    } catch (error) {
      return 'collaborator';
    }
  }

  // Verifica se o usuário é SuperAdmin
  static isSuperAdmin(): boolean {
    return this.getUserRole() === 'superadmin';
  }

  /** Claim no JWT; após liberação pelo SuperAdmin, o perfil pode estar atualizado antes do próximo login. */
  static canAccessFinanceiroFromToken(): boolean {
    try {
      const token = this.getToken();
      if (!token) return false;
      const payload = this.decodeToken(token) as Record<string, unknown> | null;
      if (!payload) return false;
      const v = payload.financeiroAccess ?? payload.FinanceiroAccess;
      return v === true || v === 'true';
    } catch {
      return false;
    }
  }

  // Verifica se o usuário pode convidar outros usuários
  static canInviteUsers(): boolean {
    const role = this.getUserRole();
    return role === 'superadmin' || role === 'admin';
  }

  // Verifica se o usuário pode criar/gerenciar setores (Admin e SuperAdmin)
  static canManageSectors(): boolean {
    const role = this.getUserRole();
    return role === 'superadmin' || role === 'admin';
  }

  // Função para obter o ID do usuário atual
  static getCurrentUserId(): number | null {
    try {
      const token = this.getToken();
      if (!token) return null;

      const decodedToken = this.decodeToken(token);
      if (!decodedToken || !decodedToken.userId) return null;

      return Number(decodedToken.userId);
    } catch (error) {
      return null;
    }
  }

  // Função para obter o nome do usuário atual
  static getCurrentUserName(): string | null {
    try {
      const user = this.getUser();
      if (user && user.name) return user.name;

      const token = this.getToken();
      if (!token) return null;

      const decodedToken = this.decodeToken(token);
      if (!decodedToken) return null;

      return decodedToken.name || decodedToken.unique_name || null;
    } catch (error) {
      return null;
    }
  }
}

export default SessionService;
